import React, { useEffect, useMemo, useState, useCallback } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { db, auth, googleProvider } from "../firebase";
import { ref, onValue, push, update, remove, get } from "firebase/database";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { CURRICULUM, GRADES, GRADE_LABEL } from "../sigalArtCurriculum";

// legacy global paths (pre-auth) — migrated into the first signed-in user, then removed
const LEGACY_CLASSES = "sigalArt/classes";
const LEGACY_LESSONS = "sigalArt/lessons";
// each user owns sigalArt/users/{uid}/...
const userClassesRef = (uid) => `sigalArt/users/${uid}/classes`;
const userLessonsRef = (uid) => `sigalArt/users/${uid}/lessons`;
const userCustomRef = (uid) => `sigalArt/users/${uid}/custom`;

/** built-in curriculum for a grade + the user's own custom lesson plans */
function lessonsForGrade(grade, custom) {
  const base = CURRICULUM[grade] || [];
  const extra = (custom || [])
    .filter((c) => Number(c.grade) === Number(grade))
    .map((c) => ({ id: c.id, title: c.title, technique: c.technique || "", desc: c.desc || "", gear: c.gear || [], custom: true }));
  return [...base, ...extra];
}
const SEARCH_WORKER = "https://sigal-gear-search.miller-kfir.workers.dev";

/** inspiration/demo images for a lesson (cached per query for the session) */
async function fetchInspiration(query) {
  const key = "artimg:" + query;
  try { const c = sessionStorage.getItem(key); if (c) return JSON.parse(c); } catch (e) {}
  const r = await fetch(`${SEARCH_WORKER}?type=images&q=${encodeURIComponent(query)}`);
  const data = await r.json();
  const imgs = Array.isArray(data.results) ? data.results : [];
  try { sessionStorage.setItem(key, JSON.stringify(imgs)); } catch (e) {}
  return imgs;
}

const MONTHS_HE = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];
const DOW_HE = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
const DOW_SHORT = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];
const CLASS_COLORS = ["#e0533b", "#4f7fc4", "#2f8a6a", "#8b5cf6", "#e08a2b", "#d6457f", "#4f9d8c", "#b0843a"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

function detectSchoolYearStart(today = new Date()) {
  return today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1;
}
/** the 10 school months Sep→Jun for a given start year */
function schoolMonths(start) {
  const arr = [];
  for (let i = 0; i < 10; i++) {
    const raw = 8 + i;
    arr.push({ y: raw < 12 ? start : start + 1, m: raw % 12 });
  }
  return arr;
}
/** Jewish-holiday school vacations, computed from the Hebrew calendar (any year) */
const HEB_FMT =
  typeof Intl !== "undefined" && Intl.DateTimeFormat
    ? new Intl.DateTimeFormat("en-u-ca-hebrew", { month: "long", day: "numeric" })
    : null;
const holidayCache = {};
function holidayName(date) {
  if (!HEB_FMT) return null;
  const k = iso(date);
  if (k in holidayCache) return holidayCache[k];
  let month, day;
  try {
    const parts = HEB_FMT.formatToParts(date);
    month = parts.find((p) => p.type === "month").value;
    day = +parts.find((p) => p.type === "day").value;
  } catch (e) {
    return (holidayCache[k] = null);
  }
  const isAdar = month === "Adar" || month === "Adar II";
  let name = null;
  if (month === "Tishri" && (day === 1 || day === 2)) name = "ראש השנה";
  else if (month === "Tishri" && day === 10) name = "יום כיפור";
  else if (month === "Tishri" && day >= 14 && day <= 22) name = "סוכות";
  else if (month === "Kislev" && day >= 25) name = "חנוכה";
  else if (month === "Tevet" && day <= 2) name = "חנוכה";
  else if (isAdar && (day === 14 || day === 15)) name = "פורים";
  else if (month === "Nisan" && day >= 14 && day <= 22) name = "פסח";
  else if (month === "Iyar" && day === 5) name = "יום העצמאות";
  else if (month === "Sivan" && day === 6) name = "שבועות";
  return (holidayCache[k] = name);
}

/** all session dates (ISO) for a class within Sep 1 – Jun 30, skipping holidays */
function scheduleDates(days, start) {
  if (!days || !days.length) return [];
  const out = [];
  const end = new Date(start + 1, 5, 30);
  const d = new Date(start, 8, 1);
  while (d <= end) {
    if (days.includes(d.getDay()) && !holidayName(d)) out.push(iso(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/* ============================================================
   Page
   ============================================================ */
export const SigalArtPlan = () => {
  const [tab, setTab] = useState("classes");
  const [classes, setClasses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [custom, setCustom] = useState([]);
  const [yearStart, setYearStart] = useState(detectSchoolYearStart());
  const [authUser, setAuthUser] = useState(undefined); // undefined=loading, null=signed out
  const [authBusy, setAuthBusy] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "מערך שנתי אומנות - סיגל מילר";
    return () => { document.title = prevTitle; };
  }, []);

  useEffect(() => onAuthStateChanged(auth, (u) => setAuthUser(u || null)), []);

  const uid = authUser ? authUser.uid : null;

  // subscribe to this user's own classes + lessons + custom plans
  useEffect(() => {
    if (!uid) { setClasses([]); setLessons([]); setCustom([]); return; }
    const unsubC = onValue(ref(db, userClassesRef(uid)), (snap) => {
      const v = snap.val() || {};
      setClasses(Object.entries(v).map(([id, c]) => ({ id, ...c })).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)));
    });
    const unsubL = onValue(ref(db, userLessonsRef(uid)), (snap) => {
      const v = snap.val() || {};
      setLessons(Object.entries(v).map(([id, l]) => ({ id, ...l })));
    });
    const unsubX = onValue(ref(db, userCustomRef(uid)), (snap) => {
      const v = snap.val() || {};
      setCustom(Object.entries(v).map(([id, c]) => ({ id, ...c })).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)));
    });
    return () => { unsubC(); unsubL(); unsubX(); };
  }, [uid]);

  // one-time migration: move legacy global data into the first signed-in user
  useEffect(() => {
    if (!uid) return;
    (async () => {
      const mine = await get(ref(db, userClassesRef(uid)));
      if (mine.exists()) return;
      const legC = await get(ref(db, LEGACY_CLASSES));
      if (!legC.exists()) return;
      const legL = await get(ref(db, LEGACY_LESSONS));
      await update(ref(db, `sigalArt/users/${uid}`), { classes: legC.val() || {}, lessons: legL.val() || {} });
      await remove(ref(db, LEGACY_CLASSES));
      await remove(ref(db, LEGACY_LESSONS));
    })().catch(() => {});
  }, [uid]);

  // keep the plan holiday-clean: drop any lesson that landed on a vacation day
  useEffect(() => {
    if (!uid || !lessons.length) return;
    const bad = {};
    lessons.forEach((l) => { if (l.date && holidayName(fromIso(l.date))) bad[l.id] = null; });
    if (Object.keys(bad).length) update(ref(db, userLessonsRef(uid)), bad);
  }, [uid, lessons]);

  const signIn = async () => {
    setAuthBusy(true);
    try { await signInWithPopup(auth, googleProvider); }
    catch (e) { alert("ההתחברות נכשלה. ודאו ש-Google מופעל ב-Firebase (Authentication)."); }
    finally { setAuthBusy(false); }
  };
  const signOutNow = () => signOut(auth).catch(() => {});

  if (authUser === undefined) {
    return <Page dir="rtl"><GlobalKiller /><Centered>טוען…</Centered></Page>;
  }
  if (!authUser) {
    return (
      <Page dir="rtl"><GlobalKiller />
        <GateCenter>
          <GateWrap>
            <GateBrush>🎨</GateBrush>
            <GateTitle>מערך שנתי באומנות</GateTitle>
            <GateText>התחברו עם חשבון Google כדי לראות ולנהל את התוכנית שלכם. לכל מורה התוכנית הפרטית שלו.</GateText>
            <GoogleBtn onClick={signIn} disabled={authBusy}>
              <GBtnG>G</GBtnG>{authBusy ? "מתחבר…" : "התחברות עם Google"}
            </GoogleBtn>
          </GateWrap>
        </GateCenter>
        <Credit>כל הזכויות שמורות © לכפיר מילר</Credit>
      </Page>
    );
  }

  return (
    <Page dir="rtl">
      <GlobalKiller />
      <Inner>
        <Header>
          <TitleGroup>
            <Brushes aria-hidden="true">🎨</Brushes>
            <Title>מערך שנתי באומנות</Title>
          </TitleGroup>
        </Header>

        <UserBar>
          {authUser.photoURL ? <Avatar src={authUser.photoURL} alt="" /> : <AvatarEmoji>👤</AvatarEmoji>}
          <UserName>{authUser.displayName || (authUser.email && authUser.email.split("@")[0])}</UserName>
          <SignOut onClick={signOutNow}>יציאה</SignOut>
        </UserBar>

        <Tabs>
          <Tab $active={tab === "classes"} onClick={() => setTab("classes")}>הכיתות שלי</Tab>
          <Tab $active={tab === "calendar"} onClick={() => setTab("calendar")}>לוח שנה</Tab>
          <Tab $active={tab === "inventory"} onClick={() => setTab("inventory")}>מאגר מערכים</Tab>
        </Tabs>

        {tab === "classes" && (
          <ClassesTab uid={uid} classes={classes} lessons={lessons} custom={custom} yearStart={yearStart} setYearStart={setYearStart} />
        )}
        {tab === "calendar" && (
          <CalendarTab uid={uid} classes={classes} lessons={lessons} custom={custom} yearStart={yearStart} />
        )}
        {tab === "inventory" && <InventoryTab uid={uid} custom={custom} />}

        <div style={{ height: 64 }} />
      </Inner>
      <Credit>כל הזכויות שמורות © לכפיר מילר</Credit>
    </Page>
  );
};

/* ============================================================
   Classes tab — add classes + generate the year
   ============================================================ */
function ClassesTab({ uid, classes, lessons, custom, yearStart, setYearStart }) {
  const CLASSES_REF = userClassesRef(uid);
  const LESSONS_REF = userLessonsRef(uid);
  const [form, setForm] = useState({ name: "", school: "", grade: 1, groupSize: 25, days: [] });
  const detected = detectSchoolYearStart();

  const lessonsByClass = useMemo(() => {
    const m = {};
    lessons.forEach((l) => { (m[l.classId] = m[l.classId] || []).push(l); });
    return m;
  }, [lessons]);

  const toggleDay = (i) =>
    setForm((f) => ({ ...f, days: f.days.includes(i) ? f.days.filter((d) => d !== i) : [...f.days, i].sort() }));

  const addClass = () => {
    if (!form.school.trim() || !form.days.length) return;
    const color = CLASS_COLORS[classes.length % CLASS_COLORS.length];
    push(ref(db, CLASSES_REF), {
      name: form.name.trim() || `כיתה ${GRADE_LABEL[form.grade]}`,
      school: form.school.trim(),
      grade: Number(form.grade),
      groupSize: Number(form.groupSize) || 0,
      days: form.days,
      color,
      createdAt: Date.now(),
    });
    setForm({ name: "", school: "", grade: 1, groupSize: 25, days: [] });
  };

  const generate = (cls) => {
    const existing = {};
    (lessonsByClass[cls.id] || []).forEach((l) => { existing[l.date] = true; });
    const dates = scheduleDates(cls.days, yearStart);
    const curr = lessonsForGrade(cls.grade, custom);
    const updates = {};
    let idx = 0, added = 0;
    for (const date of dates) {
      if (!existing[date]) {
        const plan = curr[idx % curr.length] || { title: "שיעור", technique: "", desc: "", gear: [] };
        const key = push(ref(db, LESSONS_REF)).key;
        updates[key] = {
          classId: cls.id, date, grade: cls.grade,
          title: plan.title, technique: plan.technique, description: plan.desc, gear: plan.gear,
          edited: false, createdAt: Date.now(),
        };
        added++;
      }
      idx++;
    }
    if (added) update(ref(db, LESSONS_REF), updates);
  };

  // smart shuffle: lay down all curriculum lessons in random order before any
  // repeats (max variety), keep lessons the teacher already edited.
  const shuffleClass = (cls) => {
    const existing = {};
    (lessonsByClass[cls.id] || []).forEach((l) => { existing[l.date] = l; });
    const dates = scheduleDates(cls.days, yearStart);
    const curr = lessonsForGrade(cls.grade, custom);
    if (!curr.length || !dates.length) return;
    const pool = [];
    const refill = () => {
      const c = curr.slice();
      for (let i = c.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [c[i], c[j]] = [c[j], c[i]];
      }
      pool.push(...c);
    };
    const updates = {};
    let pi = 0;
    dates.forEach((date) => {
      const ex = existing[date];
      if (ex && ex.edited) return; // respect manual edits
      if (pi >= pool.length) refill();
      const plan = pool[pi++];
      if (ex) {
        updates[`${ex.id}/title`] = plan.title;
        updates[`${ex.id}/technique`] = plan.technique;
        updates[`${ex.id}/description`] = plan.desc;
        updates[`${ex.id}/gear`] = plan.gear;
      } else {
        const key = push(ref(db, LESSONS_REF)).key;
        updates[key] = {
          classId: cls.id, date, grade: cls.grade,
          title: plan.title, technique: plan.technique, description: plan.desc, gear: plan.gear,
          edited: false, createdAt: Date.now(),
        };
      }
    });
    if (Object.keys(updates).length) update(ref(db, LESSONS_REF), updates);
  };

  const clearClassLessons = (cls) => {
    const upd = {};
    (lessonsByClass[cls.id] || []).forEach((l) => { upd[l.id] = null; });
    if (Object.keys(upd).length) update(ref(db, LESSONS_REF), upd);
  };

  const deleteClass = (cls) => {
    clearClassLessons(cls);
    remove(ref(db, `${CLASSES_REF}/${cls.id}`));
  };

  return (
    <>
      <YearRow>
        <YearLabel>שנת לימודים:</YearLabel>
        <YearSelect value={yearStart} onChange={(e) => setYearStart(Number(e.target.value))}>
          <option value={detected}>ספטמבר {detected} – יוני {detected + 1}</option>
          <option value={detected + 1}>ספטמבר {detected + 1} – יוני {detected + 2}</option>
        </YearSelect>
      </YearRow>

      <Card>
        <CardTitle>הוספת כיתה</CardTitle>
        <FormGrid>
          <Field>
            <Lbl>בית ספר</Lbl>
            <Inp value={form.school} placeholder="שם בית הספר" onChange={(e) => setForm({ ...form, school: e.target.value })} />
          </Field>
          <Field>
            <Lbl>כינוי לכיתה (אופ׳)</Lbl>
            <Inp value={form.name} placeholder="למשל: ג׳2" onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field>
            <Lbl>שכבה</Lbl>
            <Sel value={form.grade} onChange={(e) => setForm({ ...form, grade: Number(e.target.value) })}>
              {GRADES.map((g) => <option key={g} value={g}>כיתה {GRADE_LABEL[g]}</option>)}
            </Sel>
          </Field>
          <Field>
            <Lbl>גודל הקבוצה</Lbl>
            <Inp type="number" min="1" value={form.groupSize} onChange={(e) => setForm({ ...form, groupSize: e.target.value })} />
          </Field>
        </FormGrid>
        <Lbl style={{ marginTop: 6 }}>ימים בשבוע (כל שיעור 45 דק׳)</Lbl>
        <DaysRow>
          {DOW_HE.map((d, i) => (
            <DayChip key={i} $on={form.days.includes(i)} onClick={() => toggleDay(i)}>{d}</DayChip>
          ))}
        </DaysRow>
        <AddBtn onClick={addClass} disabled={!form.school.trim() || !form.days.length}>＋ הוסף כיתה</AddBtn>
      </Card>

      {classes.length === 0 ? (
        <Empty>עדיין אין כיתות. הוסיפו כיתה כדי להתחיל לתכנן את השנה.</Empty>
      ) : (
        classes.map((cls) => {
          const count = (lessonsByClass[cls.id] || []).length;
          const planned = scheduleDates(cls.days, yearStart).length;
          return (
            <ClassCard key={cls.id} style={{ borderInlineStartColor: cls.color }}>
              <ClassTop>
                <div>
                  <ClassName>{cls.name} · כיתה {GRADE_LABEL[cls.grade]}</ClassName>
                  <ClassMeta>{cls.school} · {cls.groupSize} תלמידים · {cls.days.map((d) => DOW_SHORT[d]).join(" ")}</ClassMeta>
                </div>
                <DelBtn onClick={() => deleteClass(cls)} title="מחק כיתה">✕</DelBtn>
              </ClassTop>
              <ClassActions>
                <GenBtn onClick={() => generate(cls)}>
                  {count > 0 ? "השלם מערכים חסרים" : "צור תוכנית שנתית"}
                </GenBtn>
                {count > 0 && (
                  <>
                    <ShuffleBtn onClick={() => shuffleClass(cls)} title="ערבוב חכם — מגוון מרבי, שומר על שיעורים שערכת">🔀 ערבב</ShuffleBtn>
                    <Counted>{count} / {planned} שיעורים</Counted>
                    <GhostBtn onClick={() => clearClassLessons(cls)}>נקה</GhostBtn>
                  </>
                )}
              </ClassActions>
            </ClassCard>
          );
        })
      )}
    </>
  );
}

/* ============================================================
   Calendar tab — month grid with filters + day editing
   ============================================================ */
function CalendarTab({ uid, classes, lessons, custom, yearStart }) {
  const months = useMemo(() => schoolMonths(yearStart), [yearStart]);
  const [mi, setMi] = useState(() => {
    const t = new Date();
    const idx = months.findIndex((x) => x.y === t.getFullYear() && x.m === t.getMonth());
    return idx >= 0 ? idx : 0;
  });
  const [fSchool, setFSchool] = useState("all");
  const [fGrade, setFGrade] = useState("all");
  const [fClass, setFClass] = useState("all");
  const [editing, setEditing] = useState(null);
  const [presenting, setPresenting] = useState(null);

  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c])), [classes]);
  const schools = useMemo(() => [...new Set(classes.map((c) => c.school))], [classes]);

  const visibleLessons = useMemo(() => {
    return lessons.filter((l) => {
      const c = classById[l.classId];
      if (!c) return false;
      if (fSchool !== "all" && c.school !== fSchool) return false;
      if (fGrade !== "all" && String(c.grade) !== fGrade) return false;
      if (fClass !== "all" && l.classId !== fClass) return false;
      return true;
    });
  }, [lessons, classById, fSchool, fGrade, fClass]);

  const byDate = useMemo(() => {
    const m = {};
    visibleLessons.forEach((l) => { (m[l.date] = m[l.date] || []).push(l); });
    return m;
  }, [visibleLessons]);

  const cur = months[mi];
  const first = new Date(cur.y, cur.m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(cur.y, cur.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(<DayCell key={`e${i}`} $empty />);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateObj = new Date(cur.y, cur.m, d);
    const dateIso = iso(dateObj);
    const items = byDate[dateIso] || [];
    const hol = holidayName(dateObj);
    cells.push(
      <DayCell key={d} $vac={!!hol}>
        <DayNum>{d}</DayNum>
        {hol && <VacBadge title={`חופשה: ${hol}`}>🏖️ {hol}</VacBadge>}
        {items.map((l) => {
          const c = classById[l.classId];
          return (
            <ChipWrap key={l.id}>
              <Chip style={{ background: (c?.color || "#888") + "22", borderInlineStartColor: c?.color || "#888" }}
                onClick={() => setEditing(l)}>
                <ChipClass>{c?.name}</ChipClass>{l.title}
              </Chip>
              <ChipTip>
                <TipTitle>{l.title}</TipTitle>
                <TipMeta>{c?.name} · כיתה {GRADE_LABEL[l.grade]} · 45 דק׳</TipMeta>
                {l.technique && <TipLine><b>🎯 נלמד:</b> {l.technique}</TipLine>}
                {l.description && <TipLine><b>✏️ נעשה:</b> {l.description}</TipLine>}
                <TipBtn onClick={(e) => { e.stopPropagation(); setPresenting(l); }}>▶ הצג מצגת</TipBtn>
              </ChipTip>
            </ChipWrap>
          );
        })}
      </DayCell>
    );
  }

  return (
    <>
      <Filters>
        <FSel value={fSchool} onChange={(e) => setFSchool(e.target.value)}>
          <option value="all">כל בתי הספר</option>
          {schools.map((s) => <option key={s} value={s}>{s}</option>)}
        </FSel>
        <FSel value={fGrade} onChange={(e) => setFGrade(e.target.value)}>
          <option value="all">כל השכבות</option>
          {GRADES.map((g) => <option key={g} value={g}>כיתה {GRADE_LABEL[g]}</option>)}
        </FSel>
        <FSel value={fClass} onChange={(e) => setFClass(e.target.value)}>
          <option value="all">כל הכיתות</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </FSel>
      </Filters>

      <CalHead>
        <NavBtn onClick={() => setMi((i) => Math.max(0, i - 1))} disabled={mi === 0}>‹</NavBtn>
        <CalMonth>{MONTHS_HE[cur.m]} {cur.y}</CalMonth>
        <NavBtn onClick={() => setMi((i) => Math.min(months.length - 1, i + 1))} disabled={mi === months.length - 1}>›</NavBtn>
      </CalHead>

      <Grid>
        {DOW_SHORT.map((d) => <Dow key={d}>{d}</Dow>)}
        {cells}
      </Grid>

      {visibleLessons.length === 0 && (
        <Hint>אין שיעורים להצגה. הוסיפו כיתה ולחצו "צור תוכנית שנתית" בלשונית "הכיתות שלי".</Hint>
      )}

      {editing && (
        <LessonEditor uid={uid} custom={custom} lesson={editing} cls={classById[editing.classId]} onClose={() => setEditing(null)} />
      )}
      {presenting && (
        <LessonPresentation
          lesson={{ title: presenting.title, technique: presenting.technique, desc: presenting.description, gear: presenting.gear }}
          gradeLabel={GRADE_LABEL[presenting.grade]}
          onClose={() => setPresenting(null)}
        />
      )}
    </>
  );
}

/* ============================================================
   Lesson editor (modal)
   ============================================================ */
function LessonEditor({ uid, custom, lesson, cls, onClose }) {
  const LESSONS_REF = userLessonsRef(uid);
  const [title, setTitle] = useState(lesson.title || "");
  const [desc, setDesc] = useState(lesson.description || "");
  const [gear, setGear] = useState(lesson.gear || []);
  const [technique, setTechnique] = useState(lesson.technique || "");
  const [newGear, setNewGear] = useState("");
  const [present, setPresent] = useState(false);

  const relevant = lessonsForGrade(lesson.grade, custom);
  const pickPlan = (idx) => {
    const l = relevant[idx];
    if (!l) return;
    setTitle(l.title); setTechnique(l.technique); setDesc(l.desc); setGear(l.gear);
  };

  const save = () => {
    update(ref(db, `${LESSONS_REF}/${lesson.id}`), {
      title: title.trim() || "שיעור", technique, description: desc, gear, edited: true,
    });
    onClose();
  };
  const del = () => { remove(ref(db, `${LESSONS_REF}/${lesson.id}`)); onClose(); };
  const addGear = () => { const g = newGear.trim(); if (g) { setGear([...gear, g]); setNewGear(""); } };

  const d = fromIso(lesson.date);
  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <ModalHead>
          <div>
            <ModalDate>{DOW_HE[d.getDay()]} · {d.getDate()} ב{MONTHS_HE[d.getMonth()]}</ModalDate>
            <ModalCls style={{ color: cls?.color }}>{cls?.name} · כיתה {GRADE_LABEL[lesson.grade]} · 45 דק׳</ModalCls>
          </div>
          <DelBtn onClick={onClose}>✕</DelBtn>
        </ModalHead>
        {technique && <Tech>טכניקה: {technique}</Tech>}
        <Lbl>בחר מערך מהמאגר (כיתה {GRADE_LABEL[lesson.grade]} · {relevant.length} מערכים)</Lbl>
        <Sel style={{ width: "100%" }} value="" onChange={(e) => { if (e.target.value !== "") pickPlan(Number(e.target.value)); }}>
          <option value="">— החלף למערך מתאים —</option>
          {relevant.map((l, i) => <option key={i} value={i}>{i + 1}. {l.title} · {l.technique}</option>)}
        </Sel>
        <Lbl style={{ marginTop: 8 }}>נושא השיעור</Lbl>
        <Inp value={title} onChange={(e) => setTitle(e.target.value)} />
        <Lbl style={{ marginTop: 8 }}>תיאור / מהלך</Lbl>
        <Area value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} />
        <Lbl style={{ marginTop: 8 }}>ציוד נדרש</Lbl>
        <GearWrap>
          {gear.map((g, i) => (
            <GearTag key={i}>{g}<GearX onClick={() => setGear(gear.filter((_, j) => j !== i))}>✕</GearX></GearTag>
          ))}
        </GearWrap>
        <GearAdd>
          <Inp value={newGear} placeholder="הוסף פריט ציוד…" onChange={(e) => setNewGear(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addGear(); }} />
          <SmallBtn onClick={addGear}>הוסף</SmallBtn>
        </GearAdd>
        <PresentBtn onClick={() => setPresent(true)}>▶ מצגת לכיתה</PresentBtn>
        <ModalActions>
          <SaveBtn onClick={save}>שמירה</SaveBtn>
          <GhostBtn onClick={del}>מחק שיעור</GhostBtn>
        </ModalActions>
      </Modal>
      {present && (
        <LessonPresentation
          lesson={{ title, technique, desc, gear }}
          gradeLabel={GRADE_LABEL[lesson.grade]}
          onClose={() => setPresent(false)}
        />
      )}
    </Overlay>
  );
}

/* ============================================================
   Lesson presentation — fullscreen slideshow to show the class
   ============================================================ */
function LessonPresentation({ lesson, gradeLabel, onClose }) {
  const { title, technique, desc, gear = [] } = lesson;
  const SLIDES = 5;
  const [slide, setSlide] = useState(0);
  const [images, setImages] = useState(null);

  const next = useCallback(() => setSlide((s) => Math.min(SLIDES - 1, s + 1)), []);
  const prev = useCallback(() => setSlide((s) => Math.max(0, s - 1)), []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") next();
      else if (e.key === "ArrowRight") prev();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [next, prev, onClose]);

  useEffect(() => {
    if (slide === 3 && images === null) {
      fetchInspiration(`${title} ${technique}`).then(setImages).catch(() => setImages([]));
    }
  }, [slide, images, title, technique]);

  const q = encodeURIComponent(`${title} ${technique} אומנות`);
  const googleImages = `https://www.google.com/search?tbm=isch&hl=he&q=${q}`;
  const youtube = `https://www.youtube.com/results?search_query=${q}`;

  const steps = [
    { t: "פתיחה · 5 דק׳", d: "מציגים את הנושא ומסתכלים יחד בדוגמאות השראה." },
    { t: "הדגמה · 10 דק׳", d: `המורה מדגימה את הטכניקה: ${technique}.` },
    { t: "יצירה · 25 דק׳", d: desc },
    { t: "סיכום · 5 דק׳", d: "תולים את העבודות, מסתכלים יחד ומשתפים תחושות." },
  ];

  const handleDownload = async () => {
    let imgs = images;
    if (imgs === null) {
      try { imgs = await fetchInspiration(`${title} ${technique}`); setImages(imgs); } catch (e) { imgs = []; }
    }
    const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const win = window.open("", "_blank");
    if (!win) return;
    const imgTags = (imgs || []).slice(0, 6).map((im) => `<img src="${esc(im.original || im.thumbnail)}" alt="">`).join("");
    const stepRows = steps.map((s, i) => `<div class="step"><div class="n">${i + 1}</div><div><b>${esc(s.t)}</b><div>${esc(s.d)}</div></div></div>`).join("");
    const gearTags = gear.map((g) => `<span class="tag">${esc(g)}</span>`).join("");
    win.document.write(`<!doctype html><html dir="rtl" lang="he"><head><meta charset="utf-8"><title>${esc(title)}</title>
    <style>
      @page { size: landscape; margin: 0; }
      *{ box-sizing:border-box; font-family:Heebo,Arial,sans-serif; }
      body{ margin:0; color:#241a2e; }
      .page{ width:100%; min-height:100vh; padding:6vh 8vw; display:flex; flex-direction:column; justify-content:center; page-break-after:always; }
      .page:last-child{ page-break-after:auto; }
      h1{ font-size:46px; margin:0 0 12px; }
      h2{ font-size:18px; color:#b06a2b; letter-spacing:1px; margin:0 0 18px; }
      h3{ font-size:30px; margin:0 0 10px; }
      p{ font-size:22px; line-height:1.6; }
      .chip{ display:inline-block; background:#f3eefc; border:2px solid #c9b6ee; border-radius:999px; padding:6px 18px; font-size:20px; font-weight:800; }
      .meta{ color:#7a5bbf; font-weight:800; font-size:20px; margin-top:12px; }
      .emoji{ font-size:80px; }
      .title{ text-align:center; align-items:center; }
      .step{ display:flex; gap:14px; align-items:flex-start; background:#faf4ec; border:1px solid #ecdcc6; border-radius:12px; padding:13px 16px; margin-bottom:12px; }
      .step .n{ flex:0 0 auto; width:34px; height:34px; border-radius:50%; background:#e0a93e; color:#fff; font-weight:900; display:flex; align-items:center; justify-content:center; }
      .step b{ color:#b06a2b; font-size:20px; }
      .step div div{ font-size:18px; }
      .grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
      .grid img{ width:100%; height:230px; object-fit:cover; border-radius:12px; border:2px solid #eee; }
      .tags{ display:flex; flex-wrap:wrap; gap:12px; }
      .tag{ background:#eef6f1; border:2px solid #bfe6d2; color:#2f7d62; border-radius:999px; padding:10px 22px; font-size:22px; font-weight:800; }
    </style></head><body>
      <section class="page title"><div class="emoji">🎨</div><h1>${esc(title)}</h1><div class="chip">${esc(technique)}</div><div class="meta">כיתה ${esc(gradeLabel)} · שיעור אומנות · 45 דקות</div></section>
      <section class="page"><h2>מה נלמד היום?</h2><h3>${esc(technique)}</h3><p>${esc(desc)}</p></section>
      <section class="page"><h2>מהלך השיעור</h2>${stepRows}</section>
      <section class="page"><h2>השראה ודוגמאות</h2><div class="grid">${imgTags}</div></section>
      <section class="page"><h2>הציוד שנצטרך</h2><div class="tags">${gearTags}</div></section>
    </body></html>`);
    win.document.close();
    const go = () => { try { win.focus(); win.print(); } catch (e) {} };
    win.onload = go;
    setTimeout(go, 1200);
  };

  return (
    <PresOverlay>
      <PresTop>
        <PresIcon onClick={handleDownload} title="הורד / הדפס כ-PDF">⬇</PresIcon>
        <PresIcon onClick={onClose} title="סגור">✕</PresIcon>
      </PresTop>
      <Slide key={slide}>
        {slide === 0 && (
          <>
            <SBrush>🎨</SBrush>
            <SBig>{title}</SBig>
            <STechChip>{technique}</STechChip>
            <SMeta>כיתה {gradeLabel} · שיעור אומנות · 45 דקות</SMeta>
          </>
        )}
        {slide === 1 && (
          <>
            <SKicker>מה נלמד היום?</SKicker>
            <SMid>{technique}</SMid>
            <SBody>{desc}</SBody>
          </>
        )}
        {slide === 2 && (
          <>
            <SKicker>מהלך השיעור</SKicker>
            <Steps>
              {steps.map((s, i) => (
                <Step key={i}>
                  <StepN>{i + 1}</StepN>
                  <div>
                    <StepT>{s.t}</StepT>
                    <StepD>{s.d}</StepD>
                  </div>
                </Step>
              ))}
            </Steps>
          </>
        )}
        {slide === 3 && (
          <>
            <SKicker>השראה ודוגמאות</SKicker>
            {images === null ? (
              <PresLoading>טוען דוגמאות…</PresLoading>
            ) : images.length ? (
              <ImgGrid>
                {images.slice(0, 6).map((im, i) => (
                  <ImgThumb key={i} href={im.link || im.original} target="_blank" rel="noopener noreferrer" title={im.title}>
                    <img src={im.thumbnail} alt={im.title || "השראה"} loading="lazy" />
                  </ImgThumb>
                ))}
              </ImgGrid>
            ) : (
              <SBody>לא נטענו תמונות — אפשר לפתוח חיפוש בלחצנים למטה.</SBody>
            )}
            <PresLinks>
              <PresLink href={googleImages} target="_blank" rel="noopener noreferrer">🖼️ עוד תמונות</PresLink>
              <PresLink href={youtube} target="_blank" rel="noopener noreferrer">▶ סרטון הדגמה</PresLink>
            </PresLinks>
          </>
        )}
        {slide === 4 && (
          <>
            <SKicker>הציוד שנצטרך</SKicker>
            <GearBig>
              {gear.map((g, i) => <GearBigTag key={i}>{g}</GearBigTag>)}
            </GearBig>
            <SMeta>מוכנים? יוצרים! 🎨</SMeta>
          </>
        )}
      </Slide>

      <PresNav>
        <NavArrow onClick={next} disabled={slide === SLIDES - 1}>‹ הבא</NavArrow>
        <Dots>
          {Array.from({ length: SLIDES }).map((_, i) => (
            <Dot key={i} $on={i === slide} onClick={() => setSlide(i)} />
          ))}
        </Dots>
        <NavArrow onClick={prev} disabled={slide === 0}>הקודם ›</NavArrow>
      </PresNav>
    </PresOverlay>
  );
}

/* ============================================================
   Inventory tab — browse curriculum by grade
   ============================================================ */
function InventoryTab({ uid, custom }) {
  const [grade, setGrade] = useState(1);
  const [present, setPresent] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", technique: "", desc: "", gear: "" });

  const builtin = CURRICULUM[grade] || [];
  const mine = (custom || []).filter((c) => Number(c.grade) === Number(grade));

  const addCustom = () => {
    if (!form.title.trim()) return;
    push(ref(db, userCustomRef(uid)), {
      grade,
      title: form.title.trim(),
      technique: form.technique.trim(),
      desc: form.desc.trim(),
      gear: form.gear.split(",").map((s) => s.trim()).filter(Boolean),
      createdAt: Date.now(),
    });
    setForm({ title: "", technique: "", desc: "", gear: "" });
    setAdding(false);
  };
  const delCustom = (id) => remove(ref(db, `${userCustomRef(uid)}/${id}`));

  return (
    <>
      <GradeTabs>
        {GRADES.map((g) => (
          <GradeTab key={g} $active={g === grade} onClick={() => setGrade(g)}>כיתה {GRADE_LABEL[g]}</GradeTab>
        ))}
      </GradeTabs>

      <InvHintRow>
        <InvHint>{builtin.length + mine.length} מערכים לכיתה {GRADE_LABEL[grade]}{mine.length ? ` (כולל ${mine.length} משלך)` : ""}. לחצו ▶ מצגת כדי להציג לכיתה.</InvHint>
        <AddCustomBtn onClick={() => setAdding((a) => !a)}>{adding ? "סגור" : "＋ מערך משלי"}</AddCustomBtn>
      </InvHintRow>

      {adding && (
        <Card>
          <CardTitle>מערך חדש משלי · כיתה {GRADE_LABEL[grade]}</CardTitle>
          <Field><Lbl>נושא השיעור</Lbl><Inp value={form.title} placeholder="למשל: ציור פנדה" onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field style={{ marginTop: 8 }}><Lbl>טכניקה</Lbl><Inp value={form.technique} placeholder="למשל: עפרונות צבעוניים" onChange={(e) => setForm({ ...form, technique: e.target.value })} /></Field>
          <Field style={{ marginTop: 8 }}><Lbl>תיאור / מהלך</Lbl><Area rows={2} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} /></Field>
          <Field style={{ marginTop: 8 }}><Lbl>ציוד (מופרד בפסיקים)</Lbl><Inp value={form.gear} placeholder="דפי ציור, צבעי גואש, מכחולים" onChange={(e) => setForm({ ...form, gear: e.target.value })} /></Field>
          <AddBtn style={{ marginTop: 14 }} onClick={addCustom} disabled={!form.title.trim()}>שמירת מערך</AddBtn>
        </Card>
      )}

      {builtin.map((l, i) => (
        <InvCard key={"b" + i}>
          <InvNum>{i + 1}</InvNum>
          <div style={{ flex: 1 }}>
            <InvTitle>{l.title}</InvTitle>
            <InvTech>{l.technique} · 45 דק׳</InvTech>
            <InvDesc>{l.desc}</InvDesc>
            <InvGear>{l.gear.map((g, j) => <GearTag key={j} as="span">{g}</GearTag>)}</InvGear>
          </div>
          <PresMini onClick={() => setPresent(l)} title="מצגת לכיתה">▶ מצגת</PresMini>
        </InvCard>
      ))}

      {mine.map((l) => (
        <InvCard key={l.id}>
          <InvNum style={{ background: "#fdeccd", color: "#c2452d" }}>★</InvNum>
          <div style={{ flex: 1 }}>
            <InvTitle>{l.title} <CustomTag>משלי</CustomTag></InvTitle>
            <InvTech>{l.technique || "—"} · 45 דק׳</InvTech>
            <InvDesc>{l.desc}</InvDesc>
            <InvGear>{(l.gear || []).map((g, j) => <GearTag key={j} as="span">{g}</GearTag>)}</InvGear>
          </div>
          <InvActions>
            <PresMini onClick={() => setPresent(l)} title="מצגת לכיתה">▶ מצגת</PresMini>
            <DelBtn onClick={() => delCustom(l.id)} title="מחק מערך">✕</DelBtn>
          </InvActions>
        </InvCard>
      ))}

      {present && (
        <LessonPresentation
          lesson={{ title: present.title, technique: present.technique, desc: present.desc, gear: present.gear }}
          gradeLabel={GRADE_LABEL[grade]}
          onClose={() => setPresent(null)}
        />
      )}
    </>
  );
}

/* ============================================================
   Styles
   ============================================================ */
const fadeIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}`;
const GlobalKiller = createGlobalStyle`
  body { background: #fbf7f0; }
  #root * { box-sizing: border-box; }
`;

const Page = styled.div`
  min-height: 100%;
  background:
    radial-gradient(900px 480px at 12% -5%, rgba(255,138,101,.16), transparent 60%),
    radial-gradient(900px 480px at 92% 0%, rgba(126,178,235,.16), transparent 60%),
    linear-gradient(180deg, #fbf7f0 0%, #f4ece0 100%);
  font-family: "Heebo","Assistant",system-ui,sans-serif;
  color: #2c2640;
`;
const Inner = styled.div`max-width: 880px; margin: 0 auto; padding: 20px 14px 0;`;

const Credit = styled.div`
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 5;
  text-align: center; font-size: 11px; font-weight: 600; color: #a89c8a;
  padding: 7px 8px; pointer-events: none;
  background: linear-gradient(to top, rgba(244,236,224,0.95), rgba(244,236,224,0));
`;

const Centered = styled.div`
  min-height: 60vh; display: flex; align-items: center; justify-content: center;
  font-family: "Heebo", sans-serif; font-size: 18px; font-weight: 700; color: #9a8f80;
`;
const GateCenter = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 16px;
`;
const GateWrap = styled.div`
  width: 100%; max-width: 460px; padding: 32px 24px; text-align: center;
  background: #fff; border: 1px solid #efe4d4; border-radius: 20px;
  box-shadow: 0 10px 40px rgba(80,60,30,.1);
`;
const GateBrush = styled.div`font-size: 56px; line-height: 1; margin-bottom: 6px;`;
const GateTitle = styled.h1`
  margin: 0; font-size: 30px; font-weight: 900; text-align: center;
  background: linear-gradient(100deg,#e0533b,#e08a2b,#4f9d8c,#4f7fc4,#8b5cf6);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
`;
const GateSub = styled.div`font-size: 18px; font-weight: 800; color: #7a5bbf; margin-top: 2px;`;
const GateText = styled.p`font-size: 15px; font-weight: 600; color: #8a7c6a; line-height: 1.7; margin: 16px 0 22px;`;
const GoogleBtn = styled.button`
  display: inline-flex; align-items: center; gap: 10px; border: 2px solid #e6d9c6; cursor: pointer;
  font-family: inherit; font-size: 16px; font-weight: 800; color: #3c3744; background: #fff;
  padding: 11px 26px; border-radius: 12px; box-shadow: 0 4px 14px rgba(80,60,30,.1);
  &:hover:not(:disabled){ background: #faf4ec; } &:disabled{ opacity:.6; cursor:not-allowed; }
`;
const GBtnG = styled.span`
  font-family: Arial, sans-serif; font-weight: 900; font-size: 18px;
  background: conic-gradient(from -45deg, #ea4335, #fbbc05, #34a853, #4285f4, #ea4335);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
`;
const UserBar = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
`;
const Avatar = styled.img`width: 30px; height: 30px; border-radius: 50%; border: 2px solid #efe4d4;`;
const AvatarEmoji = styled.div`
  width: 30px; height: 30px; border-radius: 50%; background: #f3eefc; display: flex;
  align-items: center; justify-content: center; font-size: 16px;
`;
const UserName = styled.div`font-size: 14px; font-weight: 800; color: #5a5060; flex: 1;`;
const SignOut = styled.button`
  border: 1.5px solid #e6d9c6; background: #fff; color: #8a7c6a; font-family: inherit; font-weight: 700;
  font-size: 13px; padding: 7px 14px; border-radius: 10px; cursor: pointer; &:hover{ background:#faf4ec; }
`;

const Header = styled.div`
  direction: rtl; display: flex; align-items: baseline; justify-content: center;
  flex-wrap: wrap; gap: 4px 12px; margin-bottom: 14px;
`;
const TitleGroup = styled.div`direction: ltr; display: inline-flex; align-items: baseline; gap: 10px;`;
const Brushes = styled.div`font-size: clamp(26px,6vw,34px); align-self: center;`;
const Title = styled.h1`
  margin: 0; font-size: clamp(22px,5vw,34px); font-weight: 900; white-space: nowrap;
  background: linear-gradient(100deg,#e0533b,#e08a2b,#4f9d8c,#4f7fc4,#8b5cf6);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
`;
const Subtitle = styled.div`font-size: clamp(14px,3.6vw,18px); font-weight: 800; color: #7a5bbf; white-space: nowrap;`;

const Tabs = styled.div`display: flex; gap: 6px; margin-bottom: 16px; flex-wrap: wrap;`;
const Tab = styled.button`
  flex: 1; min-width: 110px; border: 2px solid ${(p) => (p.$active ? "#e0533b" : "#e6d9c6")};
  background: ${(p) => (p.$active ? "#e0533b" : "#fff")}; color: ${(p) => (p.$active ? "#fff" : "#6b5a44")};
  font-family: inherit; font-weight: 800; font-size: 15px; padding: 10px; border-radius: 12px; cursor: pointer;
  transition: .15s;
`;

const YearRow = styled.div`display: flex; align-items: center; gap: 10px; margin-bottom: 12px;`;
const YearLabel = styled.div`font-weight: 800; font-size: 14px; color: #6b5a44;`;
const YearSelect = styled.select`
  font-family: inherit; font-weight: 700; font-size: 14px; padding: 8px 10px; border-radius: 10px;
  border: 1.5px solid #e6d9c6; background: #fff; color: #2c2640;
`;

const Card = styled.div`
  background: #fff; border: 1px solid #efe4d4; border-radius: 16px; padding: 16px; margin-bottom: 14px;
  box-shadow: 0 4px 16px rgba(80,60,30,.06); animation: ${fadeIn} .25s ease;
`;
const CardTitle = styled.div`font-weight: 900; font-size: 18px; margin-bottom: 12px; color: #2c2640;`;
const FormGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 10px; @media (max-width:560px){grid-template-columns:1fr;}`;
const Field = styled.div`display: flex; flex-direction: column; gap: 4px;`;
const Lbl = styled.label`font-size: 12px; font-weight: 700; color: #b0a392;`;
const Inp = styled.input`
  font-family: inherit; font-size: 15px; font-weight: 600; color: #2c2640;
  border: 1.5px solid #e6d9c6; border-radius: 10px; padding: 9px 11px; outline: none; width: 100%;
  &:focus{border-color:#e08a2b;}
`;
const Sel = styled.select`
  font-family: inherit; font-size: 15px; font-weight: 700; color: #2c2640;
  border: 1.5px solid #e6d9c6; border-radius: 10px; padding: 9px 11px; outline: none; background: #fff;
`;
const Area = styled.textarea`
  font-family: inherit; font-size: 14px; color: #2c2640; border: 1.5px solid #e6d9c6; border-radius: 10px;
  padding: 9px 11px; outline: none; width: 100%; resize: vertical; &:focus{border-color:#e08a2b;}
`;
const DaysRow = styled.div`display: flex; gap: 6px; flex-wrap: wrap; margin: 6px 0 14px;`;
const DayChip = styled.button`
  border: 1.5px solid ${(p) => (p.$on ? "#2f8a6a" : "#e6d9c6")};
  background: ${(p) => (p.$on ? "#2f8a6a" : "#faf4ec")}; color: ${(p) => (p.$on ? "#fff" : "#6b5a44")};
  font-family: inherit; font-weight: 700; font-size: 13px; padding: 7px 11px; border-radius: 999px; cursor: pointer;
`;
const AddBtn = styled.button`
  border: none; cursor: pointer; font-family: inherit; font-size: 16px; font-weight: 800; color: #fff;
  padding: 10px 22px; border-radius: 12px; background: linear-gradient(135deg,#e0533b,#e08a2b);
  box-shadow: 0 6px 16px rgba(224,83,59,.3); &:disabled{opacity:.5;cursor:not-allowed;}
`;

const Empty = styled.div`
  text-align: center; padding: 40px 20px; color: #9a8f80; font-weight: 600; line-height: 1.8;
  background: #fff; border: 2px dashed #e6d9c6; border-radius: 16px;
`;
const ClassCard = styled.div`
  background: #fff; border: 1px solid #efe4d4; border-inline-start: 6px solid #888; border-radius: 14px;
  padding: 14px; margin-bottom: 10px; box-shadow: 0 3px 12px rgba(80,60,30,.05);
`;
const ClassTop = styled.div`display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;`;
const ClassName = styled.div`font-weight: 900; font-size: 17px;`;
const ClassMeta = styled.div`font-size: 13px; font-weight: 600; color: #9a8f80; margin-top: 2px;`;
const ClassActions = styled.div`display: flex; align-items: center; gap: 10px; margin-top: 12px; flex-wrap: wrap;`;
const GenBtn = styled.button`
  border: none; cursor: pointer; font-family: inherit; font-weight: 800; font-size: 14px; color: #fff;
  padding: 9px 16px; border-radius: 10px; background: linear-gradient(135deg,#4f7fc4,#4f9d8c);
`;
const ShuffleBtn = styled.button`
  border: 1.5px solid #8b5cf6; background: #f3eefc; color: #6d3fc4; font-family: inherit; font-weight: 800;
  font-size: 13px; padding: 8px 12px; border-radius: 10px; cursor: pointer; &:hover{ background:#e9defc; }
`;
const Counted = styled.div`font-size: 13px; font-weight: 700; color: #6b5a44;`;
const GhostBtn = styled.button`
  border: 1.5px solid #e6d9c6; background: #fff; color: #c0654a; font-family: inherit; font-weight: 700;
  font-size: 13px; padding: 8px 12px; border-radius: 10px; cursor: pointer;
`;
const DelBtn = styled.button`
  flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%; border: none; cursor: pointer;
  background: #f7ede3; color: #c0654a; font-size: 14px; font-weight: 900;
`;

const Filters = styled.div`display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap;`;
const FSel = styled.select`
  flex: 1; min-width: 120px; font-family: inherit; font-weight: 700; font-size: 14px; padding: 9px 10px;
  border-radius: 10px; border: 1.5px solid #e6d9c6; background: #fff; color: #2c2640;
`;
const CalHead = styled.div`display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 10px;`;
const CalMonth = styled.div`font-weight: 900; font-size: 18px; min-width: 150px; text-align: center;`;
const NavBtn = styled.button`
  width: 36px; height: 36px; border-radius: 10px; border: 1.5px solid #e6d9c6; background: #fff;
  font-size: 20px; font-weight: 800; color: #6b5a44; cursor: pointer; &:disabled{opacity:.35;cursor:default;}
`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(7,1fr); gap: 4px;`;
const Dow = styled.div`text-align: center; font-weight: 800; font-size: 12px; color: #b0a392; padding: 4px 0;`;
const DayCell = styled.div`
  position: relative;
  min-height: 76px;
  background: ${(p) => (p.$empty ? "transparent" : p.$vac ? "#fbeedd" : "#fff")};
  border: ${(p) => (p.$empty ? "none" : p.$vac ? "1px dashed #e3b277" : "1px solid #efe4d4")};
  border-radius: 10px; padding: 4px;
  display: flex; flex-direction: column; gap: 3px;
`;
const DayNum = styled.div`font-size: 12px; font-weight: 800; color: #b0a392; text-align: center;`;
const VacBadge = styled.div`
  font-size: 9.5px; font-weight: 800; color: #a85f23; background: #f6dcbb;
  border-radius: 5px; padding: 2px 4px; line-height: 1.2; text-align: center;
`;
const Chip = styled.button`
  border: none; border-inline-start: 3px solid #888; text-align: right; cursor: pointer;
  font-family: inherit; font-size: 10.5px; font-weight: 700; color: #2c2640; border-radius: 5px;
  padding: 3px 5px; line-height: 1.25; overflow: hidden; display: block; width: 100%;
`;
const ChipClass = styled.span`display: block; font-size: 9px; font-weight: 800; opacity: .65;`;
const ChipTip = styled.div`
  display: none; position: absolute; bottom: 100%; right: 0; margin-bottom: 7px; z-index: 40;
  width: max-content; max-width: 260px; background: #2c2150; color: #fff8ec;
  border-radius: 10px; padding: 10px 12px; box-shadow: 0 10px 26px rgba(0,0,0,.4); text-align: right;
  /* transparent bridge over the gap so the pointer can reach the button without
     leaving the hover area */
  &::after { content: ""; position: absolute; top: 100%; left: 0; right: 0; height: 11px; }
`;
const ChipWrap = styled.div`
  position: relative;
  &:hover ${ChipTip}, ${ChipTip}:hover { display: block; }
`;
const TipTitle = styled.div`font-weight: 900; font-size: 13px; line-height: 1.3;`;
const TipMeta = styled.div`font-size: 11px; font-weight: 700; color: #cdbff0; margin: 2px 0 7px;`;
const TipLine = styled.div`
  font-size: 11.5px; font-weight: 600; color: #ece4fb; line-height: 1.5; margin-bottom: 6px;
  b { color: #ffd27a; font-weight: 800; }
`;
const TipBtn = styled.button`
  border: none; cursor: pointer; font-family: inherit; font-weight: 800; font-size: 12px; color: #2c2150;
  background: #ffd27a; border-radius: 8px; padding: 6px 12px; width: 100%; &:hover{ filter: brightness(1.05); }
`;
const Hint = styled.div`text-align: center; color: #9a8f80; font-weight: 600; padding: 18px; line-height: 1.8;`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 50; background: rgba(20,12,30,.55); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 16px; direction: rtl;
`;
const Modal = styled.div`
  background: #fff; border-radius: 16px; padding: 18px; width: min(460px,100%); max-height: 90vh;
  overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.4); animation: ${fadeIn} .2s ease;
`;
const ModalHead = styled.div`display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;`;
const ModalDate = styled.div`font-weight: 900; font-size: 17px;`;
const ModalCls = styled.div`font-weight: 800; font-size: 13px; margin-top: 2px;`;
const Tech = styled.div`font-size: 13px; font-weight: 700; color: #7a5bbf; background: #f3eefc; border-radius: 8px; padding: 6px 10px; margin-bottom: 10px;`;
const GearWrap = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px;`;
const GearTag = styled.span`
  display: inline-flex; align-items: center; gap: 5px; background: #eef6f1; color: #2f7d62;
  border: 1px solid #bfe6d2; border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 700;
`;
const GearX = styled.span`cursor: pointer; opacity: .55; font-size: 10px; &:hover{opacity:1;}`;
const GearAdd = styled.div`display: flex; gap: 6px; margin-top: 8px;`;
const SmallBtn = styled.button`
  border: none; cursor: pointer; font-family: inherit; font-weight: 800; font-size: 13px; color: #fff;
  padding: 0 16px; border-radius: 10px; background: #7a5bbf; white-space: nowrap;
`;
const ModalActions = styled.div`display: flex; gap: 10px; margin-top: 16px;`;
const SaveBtn = styled.button`
  flex: 1; border: none; cursor: pointer; font-family: inherit; font-weight: 800; font-size: 15px; color: #fff;
  padding: 11px; border-radius: 12px; background: linear-gradient(135deg,#2f8a6a,#4f9d8c);
`;

const GradeTabs = styled.div`display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 12px;`;
const GradeTab = styled.button`
  border: 2px solid ${(p) => (p.$active ? "#8b5cf6" : "#e6d9c6")}; background: ${(p) => (p.$active ? "#8b5cf6" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#6b5a44")}; font-family: inherit; font-weight: 800; font-size: 14px;
  padding: 8px 14px; border-radius: 999px; cursor: pointer;
`;
const InvHint = styled.div`font-size: 13px; font-weight: 600; color: #9a8f80; line-height: 1.7; flex: 1;`;
const InvHintRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 12px;
`;
const AddCustomBtn = styled.button`
  flex: 0 0 auto; border: 2px solid #8b5cf6; background: #f3eefc; color: #6d3fc4; font-family: inherit;
  font-weight: 800; font-size: 14px; padding: 9px 16px; border-radius: 10px; cursor: pointer; white-space: nowrap;
  &:hover{ background:#e9defc; }
`;
const CustomTag = styled.span`
  font-size: 11px; font-weight: 800; color: #c2452d; background: #fdeccd; border-radius: 999px; padding: 2px 8px;
`;
const InvActions = styled.div`display: flex; flex-direction: column; gap: 6px; align-items: stretch;`;
const InvCard = styled.div`
  display: flex; gap: 12px; background: #fff; border: 1px solid #efe4d4; border-radius: 14px; padding: 14px;
  margin-bottom: 10px; box-shadow: 0 3px 12px rgba(80,60,30,.05);
`;
const InvNum = styled.div`
  flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%; background: #f3eefc; color: #8b5cf6;
  font-weight: 900; display: flex; align-items: center; justify-content: center; font-size: 14px;
`;
const InvTitle = styled.div`font-weight: 900; font-size: 16px;`;
const InvTech = styled.div`font-size: 12px; font-weight: 700; color: #7a5bbf; margin: 2px 0 6px;`;
const InvDesc = styled.div`font-size: 14px; color: #5a5060; line-height: 1.6; margin-bottom: 8px;`;
const InvGear = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const PresMini = styled.button`
  flex: 0 0 auto; align-self: flex-start; border: 2px solid #8b5cf6; background: #f3eefc; color: #6d3fc4;
  font-family: inherit; font-weight: 800; font-size: 13px; padding: 7px 12px; border-radius: 10px; cursor: pointer;
  white-space: nowrap; &:hover{ background:#e9defc; }
`;
const PresentBtn = styled.button`
  width: 100%; margin-top: 14px; border: 2px solid #8b5cf6; background: #f3eefc; color: #6d3fc4;
  font-family: inherit; font-weight: 800; font-size: 15px; padding: 11px; border-radius: 12px; cursor: pointer;
  &:hover{ background:#e9defc; }
`;

/* ----- presentation (fullscreen slideshow) ----- */
const PresOverlay = styled.div`
  position: fixed; inset: 0; z-index: 80; direction: rtl;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: radial-gradient(1200px 700px at 50% 0%, #2c2150, #14101f 75%);
  color: #fff8ec; padding: 24px; animation: ${fadeIn} .2s ease;
`;
const PresTop = styled.div`
  position: absolute; top: 14px; left: 14px; display: flex; gap: 10px; z-index: 5;
`;
const PresIcon = styled.button`
  width: 42px; height: 42px; border-radius: 50%; border: 2px solid rgba(255,255,255,.4);
  background: rgba(255,255,255,.1); color: #fff; font-size: 18px; font-weight: 900; cursor: pointer;
  &:hover{ background: rgba(255,255,255,.22); }
`;
const Slide = styled.div`
  flex: 1; min-height: 0; width: 100%; max-width: 880px; display: flex; flex-direction: column;
  align-items: center; justify-content: center; text-align: center; gap: 16px;
  overflow-y: auto; padding: 56px 8px 8px;
  animation: ${fadeIn} .3s ease;
`;
const SBrush = styled.div`font-size: clamp(48px,12vw,90px); line-height: 1;`;
const SBig = styled.div`
  font-size: clamp(34px,8vw,64px); font-weight: 900; line-height: 1.05;
  background: linear-gradient(100deg,#ffd27a,#ff8a65,#ff6f9c,#9b7bd4,#7ec4ff);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
`;
const STechChip = styled.div`
  background: rgba(255,255,255,.14); border: 2px solid rgba(255,255,255,.3); border-radius: 999px;
  padding: 8px 22px; font-size: clamp(16px,3.5vw,22px); font-weight: 800;
`;
const SMeta = styled.div`font-size: clamp(15px,3vw,20px); font-weight: 700; color: #cdbff0;`;
const SKicker = styled.div`
  font-size: clamp(16px,3vw,20px); font-weight: 800; color: #ffd27a; letter-spacing: 1px;
  text-transform: none; margin-bottom: 4px;
`;
const SMid = styled.div`font-size: clamp(28px,6vw,48px); font-weight: 900; color: #fff;`;
const SBody = styled.div`font-size: clamp(18px,4vw,28px); font-weight: 600; line-height: 1.55; max-width: 760px; color: #f3ecff;`;
const Steps = styled.div`display: flex; flex-direction: column; gap: 14px; width: 100%; max-width: 680px; text-align: right;`;
const Step = styled.div`
  display: flex; gap: 14px; align-items: flex-start; background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.16); border-radius: 14px; padding: 14px 16px;
`;
const StepN = styled.div`
  flex: 0 0 auto; width: 34px; height: 34px; border-radius: 50%; background: #ffd27a; color: #2c2150;
  font-weight: 900; display: flex; align-items: center; justify-content: center; font-size: 17px;
`;
const StepT = styled.div`font-weight: 900; font-size: clamp(16px,3vw,20px); color: #ffd27a;`;
const StepD = styled.div`font-weight: 600; font-size: clamp(15px,2.6vw,18px); color: #f3ecff; line-height: 1.5;`;
const PresLoading = styled.div`font-size: 20px; font-weight: 700; color: #cdbff0; padding: 30px;`;
const ImgGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; width: 100%; max-width: 600px;
  @media (max-width:520px){ grid-template-columns: repeat(2,1fr); }
`;
const ImgThumb = styled.a`
  display: block; aspect-ratio: 1; border-radius: 12px; overflow: hidden; background: rgba(255,255,255,.1);
  border: 2px solid rgba(255,255,255,.2); img{ width:100%; height:100%; object-fit: cover; display:block; }
  &:hover{ border-color:#ffd27a; }
`;
const PresLinks = styled.div`display: flex; gap: 12px; margin-top: 14px; flex-wrap: wrap; justify-content: center;`;
const PresLink = styled.a`
  background: rgba(255,255,255,.14); border: 2px solid rgba(255,255,255,.35); color: #fff;
  text-decoration: none; font-weight: 800; font-size: 16px; padding: 10px 20px; border-radius: 12px;
  &:hover{ background: rgba(255,255,255,.24); }
`;
const GearBig = styled.div`display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; max-width: 720px;`;
const GearBigTag = styled.div`
  background: rgba(255,255,255,.12); border: 2px solid rgba(255,255,255,.3); border-radius: 999px;
  padding: 10px 22px; font-size: clamp(16px,3.5vw,22px); font-weight: 800;
`;
const PresNav = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 20px; padding: 14px;
`;
const NavArrow = styled.button`
  border: 2px solid rgba(255,255,255,.4); background: rgba(255,255,255,.1); color: #fff;
  font-family: inherit; font-weight: 800; font-size: 16px; padding: 10px 22px; border-radius: 12px; cursor: pointer;
  &:disabled{ opacity:.3; cursor: default; }
`;
const Dots = styled.div`display: flex; gap: 8px;`;
const Dot = styled.button`
  width: 11px; height: 11px; border-radius: 50%; border: none; cursor: pointer; padding: 0;
  background: ${(p) => (p.$on ? "#ffd27a" : "rgba(255,255,255,.3)")};
`;
